import type { ChartEdge, ChartYDomain } from './types'

export interface FittedYDomain {
	min: number
	max: number
}

/**
 * Resolves the y-axis domain a chart draws against. Order is significant:
 * `include` widens the raw min/max before padding, `padPct`/`padMin` add
 * headroom around that widened span, and `min`/`max` are a hard override
 * applied last. A flat series (every value equal, and no include/pad
 * widens it) still gets a non-zero span here — without that floor the
 * y-scale downstream divides by zero and the line collapses to NaN.
 */
export function fitYDomain(values: number[], domain: ChartYDomain = {}): FittedYDomain {
	const included = domain.include?.length ? [...values, ...domain.include] : values
	const rawMin = included.length ? Math.min(...included) : 0
	const rawMax = included.length ? Math.max(...included) : 0
	const span = rawMax - rawMin
	const pad = Math.max(span * (domain.padPct ?? 0), domain.padMin ?? 0, span === 0 ? 1 : 0)

	let min = rawMin - pad
	let max = rawMax + pad
	if (domain.min !== undefined) min = domain.min
	if (domain.max !== undefined) max = domain.max
	return { min, max }
}

/** Resolves `'min'`/`'max'` sentinels against the fitted domain; a numeric edge passes through unchanged. */
export function edge(value: ChartEdge, domain: FittedYDomain): number {
	if (value === 'min') return domain.min
	if (value === 'max') return domain.max
	return value
}
